import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { QualificationModel } from "../../api/qualification/QualificationModel";
import { QualificationAdapter } from "../../api/qualification/QualificationAdapter";
import type { QualificationItem } from "../../api/qualification/Qualification.typed";

interface QualificationContextType {
  getQualificationsList: () => Promise<string[] | undefined>;
  listQualificationItems: () => Promise<QualificationItem[] | undefined>;
  createQualificationItem: (payload: {
    sigle: string;
    name?: string;
    description?: string;
  }) => Promise<void>;
  updateQualificationItem: (
    id: number,
    payload: { sigle: string; name?: string; description?: string },
  ) => Promise<void>;
  deleteQualificationItem: (id: number) => Promise<void>;
}

const QualificationContext = React.createContext<QualificationContextType>(null!);

export function QualificationProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    const model = new QualificationModel(appConfig);
    return new QualificationAdapter(model);
  }, [appConfig]);

  const wrap = React.useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      try {
        return await fn();
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return undefined;
      }
    },
    [showAlert],
  );

  const getQualificationsList = React.useCallback(
    async (): Promise<string[] | undefined> =>
      adapter ? wrap(() => adapter.getQualificationsList()) : undefined,
    [adapter, wrap],
  );

  const listQualificationItems = React.useCallback(
    async (): Promise<QualificationItem[] | undefined> =>
      adapter ? wrap(() => adapter.listQualificationItems()) : undefined,
    [adapter, wrap],
  );

  const createQualificationItem = React.useCallback(
    async (payload: { sigle: string; name?: string; description?: string }): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.createQualificationItem(payload);
        showAlert("Qualification créée", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const updateQualificationItem = React.useCallback(
    async (
      id: number,
      payload: { sigle: string; name?: string; description?: string },
    ): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.updateQualificationItem(id, payload);
        showAlert("Qualification mise à jour", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const deleteQualificationItem = React.useCallback(
    async (id: number): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.deleteQualificationItem(id);
        showAlert("Qualification supprimée", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const value = React.useMemo<QualificationContextType>(
    () => ({
      getQualificationsList,
      listQualificationItems,
      createQualificationItem,
      updateQualificationItem,
      deleteQualificationItem,
    }),
    [
      getQualificationsList,
      listQualificationItems,
      createQualificationItem,
      updateQualificationItem,
      deleteQualificationItem,
    ],
  );

  return (
    <QualificationContext.Provider value={value}>{props.children}</QualificationContext.Provider>
  );
}

export function useQualification(): QualificationContextType {
  const ctx = React.useContext(QualificationContext);
  if (!ctx) throw new Error("useQualification must be used within QualificationProvider");
  return ctx;
}
